import React from "react";
import { cn } from "@/lib/utils";

type HeadingLevel = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

type HeadingLevelWithEmphasis = HeadingLevel | "h1Emphasis";

type WebsiteStyle = "traditional" | "modernClean" | "bigBold" | "posterBold";

interface HeaderProps {
  level: HeadingLevel;
  children: React.ReactNode;
  websiteStyle: WebsiteStyle;
  emphasis?: boolean;
  className?: string;
}

export const Heading: React.FC<HeaderProps> = ({
  level = "h2",
  children,
  websiteStyle = "modernClean",
  emphasis = false,
  className,
}) => {
  const HeadingTag = level as keyof JSX.IntrinsicElements;

  const styleClasses: Record<WebsiteStyle, Record<HeadingLevelWithEmphasis, string>> = {
    traditional: {
      h1: "font-display text-5xl md:text-6xl lg:text-7xl font-bold text-primary leading-none",
      h1Emphasis:
        "font-display text-5xl md:text-6xl lg:text-7xl font-bold text-primary leading-none",
      h2: "font-display text-primary font-extrabold text-4xl md:text-5xl lg:text-6xl leading-none",
      h3: "font-display text-xl font-bold text-primary leading-none",
      h4: "font-display text-xl font-bold text-primary italic leading-none",
      h5: "font-display text-lg text-primary leading-none",
      h6: "font-display text-primary leading-none",
    },
    modernClean: {
      h1: "font-display text-5xl md:text-6xl lg:text-7xl font-bold text-primary leading-[110%]",
      h1Emphasis: "font-display text-5xl md:text-6xl lg:text-7xl text-primary leading-[110%]",
      h2: "font-display font-bold text-primary text-4xl md:text-5xl lg:text-6xl leading-[110%]",
      h3: "font-display text-2xl font-bold text-primary leading-[110%]",
      h4: "font-display text-xl font-bold text-primary leading-[110%]",
      h5: "font-display text-lg text-primary leading-[110%]",
      h6: "font-display text-primary leading-[110%]",
    },
    bigBold: {
      h1: "font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary uppercase leading-tight",
      h1Emphasis:
        "font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-primary uppercase leading-tight",
      h2: "font-display font-extrabold text-primary text-4xl md:text-5xl lg:text-6xl uppercase leading-tight",
      h3: "font-display text-2xl font-bold text-primary leading-tight",
      h4: "font-display text-xl text-primary italic leading-tight",
      h5: "font-display text-lg text-primary leading-tight",
      h6: "font-display text-primary leading-tight",
    },
    posterBold: {
      h1: "font-display font-black text-foreground tracking-tight leading-[0.9] text-[clamp(3rem,8vw,7rem)]",
      h1Emphasis:
        "font-display font-black text-foreground tracking-tight leading-[0.85] text-[clamp(6rem,18vw,20rem)]",
      h2: "font-display font-extrabold text-foreground tracking-tight leading-tight text-4xl md:text-5xl lg:text-6xl",
      h3: "font-display font-bold text-foreground leading-tight text-2xl md:text-3xl",
      h4: "font-display font-bold text-foreground leading-tight text-xl md:text-2xl",
      h5: "font-display font-semibold text-foreground leading-snug text-lg",
      h6: "font-display font-semibold text-foreground leading-snug text-base uppercase tracking-wide",
    },
  };

  const styleSet = styleClasses[websiteStyle] || styleClasses.modernClean;
  let defaultStyle = styleSet[level] || styleSet["h2"];
  if (emphasis && level === "h1") {
    defaultStyle = styleSet["h1Emphasis"];
  }

  // TODO: implement emphasisWords here to consolidate the logic a bit more
  return (
    <HeadingTag data-feedback-eligible className={cn(defaultStyle, className)}>
      {children}
    </HeadingTag>
  );
};
